import { useCallback } from 'react';
import { useTheme } from '@/contexts/GameSettingsContext';
import { getStreakState } from '@/utils/dailyStreak';

/**
 * ShareCardGenerator - Dynamic Branded Share Card Service
 * 
 * Renders a theme-aware 1080x1920 canvas (Instagram/Facebook Story optimized)
 * with the user's final score and the chromedinogame.com URL, then uses the
 * native Web Share API to trigger the system share sheet. Falls back to a
 * direct download on desktop browsers without Web Share support.
 */

const CARD_WIDTH = 1080;
const CARD_HEIGHT = 1920;
const SITE_BASE = 'chromedinogame.com';

export type BadgeType = 'standard' | 'milestone' | 'streak';

// UTM-attributed URL for share cards (includes referrer ID for ambassador credit)
function buildShareUrl(badgeType: BadgeType): string {
  const campaign = badgeType === 'milestone' ? 'milestone' : badgeType === 'streak' ? 'streak' : 'highscore';
  let referralId = 'anonymous';
  try {
    referralId = localStorage.getItem('referral_id') || referralId;
  } catch {
    // localStorage unavailable
  }
  return `https://${SITE_BASE}/?utm_source=share&utm_medium=image&utm_campaign=${campaign}&utm_content=${referralId}`;
}

interface ThemeColors {
  bg: string;
  bgAccent: string;
  text: string;
  textSecondary: string;
  accent: string;
}

function getThemeColors(theme: 'light' | 'dark'): ThemeColors {
  if (theme === 'dark') {
    return {
      bg: '#202124',
      bgAccent: '#303134',
      text: '#F1F3F4',
      textSecondary: '#9AA0A6',
      accent: '#FF6B35', // Neon orange accent
    };
  }
  return {
    bg: '#FFFFFF',
    bgAccent: '#F8F9FA',
    text: '#202124',
    textSecondary: '#5F6368',
    accent: '#FF6B35',
  };
}

// Badge-specific decorations
function getBadgeConfig(badgeType: BadgeType, score: number) {
  if (badgeType === 'milestone' || score >= 1000) {
    return {
      label: 'MILESTONE UNLOCKED',
      icon: '🏆',
      tagline: 'Joined the 1000+ Club',
    };
  }
  if (badgeType === 'streak') {
    return {
      label: '7-DAY STREAK',
      icon: '⚡',
      tagline: 'Daily Dino Devotee',
    };
  }
  return {
    label: 'NEW HIGH SCORE!',
    icon: '🦖',
    tagline: 'Beat the Browser, Beat Your Friends',
  };
}

/**
 * Renders the share card to an off-screen canvas and returns a Blob.
 */
async function renderShareCard(
  score: number,
  highScore: number,
  theme: 'light' | 'dark',
  badgeType: BadgeType = 'standard'
): Promise<Blob | null> {
  // Create off-screen canvas
  const canvas = document.createElement('canvas');
  canvas.width = CARD_WIDTH;
  canvas.height = CARD_HEIGHT;
  canvas.style.position = 'absolute';
  canvas.style.left = '-9999px';
  canvas.style.top = '-9999px';
  document.body.appendChild(canvas);

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    document.body.removeChild(canvas);
    return null;
  }

  const colors = getThemeColors(theme);

  // Background
  ctx.fillStyle = colors.bg;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  // Subtle accent strip at top
  ctx.fillStyle = colors.accent;
  ctx.fillRect(0, 0, CARD_WIDTH, 12);

  // Header: Site Brand
  ctx.fillStyle = colors.textSecondary;
  ctx.font = '600 36px "Roboto", system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('CHROME DINO GAME', CARD_WIDTH / 2, 180);

  // Decorative divider line
  ctx.strokeStyle = colors.textSecondary;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(CARD_WIDTH / 2 - 100, 220);
  ctx.lineTo(CARD_WIDTH / 2 + 100, 220);
  ctx.stroke();

  // Badge configuration
  const badge = getBadgeConfig(badgeType, score);

  // Badge icon (using text for cross-platform compatibility)
  ctx.font = '300px "Apple Color Emoji", "Segoe UI Emoji", sans-serif';
  ctx.fillText(badge.icon, CARD_WIDTH / 2, 560);

  // Badge label
  ctx.fillStyle = colors.accent;
  ctx.font = '700 56px "Roboto", system-ui, sans-serif';
  ctx.fillText(badge.label, CARD_WIDTH / 2, 720);

  // Big Score Display
  ctx.fillStyle = colors.text;
  ctx.font = '900 320px "Roboto Mono", "Courier New", monospace';
  const scoreStr = score.toLocaleString();
  ctx.fillText(scoreStr, CARD_WIDTH / 2, 1080);

  // "POINTS" label
  ctx.fillStyle = colors.textSecondary;
  ctx.font = '500 48px "Roboto", system-ui, sans-serif';
  ctx.fillText('POINTS', CARD_WIDTH / 2, 1180);

  // Stats row: Previous high score
  if (highScore > 0 && highScore !== score) {
    ctx.fillStyle = colors.textSecondary;
    ctx.font = '400 38px "Roboto", system-ui, sans-serif';
    ctx.fillText(`Previous best: ${highScore.toLocaleString()}`, CARD_WIDTH / 2, 1280);
  }

  // Call-to-action box
  const ctaY = 1480;
  const ctaPadding = 60;
  const ctaHeight = 200;
  ctx.fillStyle = colors.bgAccent;
  ctx.fillRect(ctaPadding, ctaY, CARD_WIDTH - ctaPadding * 2, ctaHeight);

  ctx.fillStyle = colors.text;
  ctx.font = '600 52px "Roboto", system-ui, sans-serif';
  ctx.fillText('Can you beat it?', CARD_WIDTH / 2, ctaY + 80);

  // URL (display only — actual share URL includes UTM)
  ctx.fillStyle = colors.accent;
  ctx.font = '700 56px "Roboto Mono", "Courier New", monospace';
  ctx.fillText(SITE_BASE, CARD_WIDTH / 2, ctaY + 160);

  // Footer with badge tagline
  ctx.fillStyle = colors.textSecondary;
  ctx.font = '400 28px "Roboto", system-ui, sans-serif';
  ctx.fillText(badge.tagline, CARD_WIDTH / 2, 1820);

  // Bottom accent strip
  ctx.fillStyle = colors.accent;
  ctx.fillRect(0, CARD_HEIGHT - 12, CARD_WIDTH, 12);

  // Convert to blob
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      document.body.removeChild(canvas);
      resolve(blob);
    }, 'image/png', 0.95);
  });
}

/**
 * Triggers the native Web Share API with the generated card.
 * Falls back to a direct download on desktop browsers.
 */
async function shareScore(
  blob: Blob,
  score: number,
  theme: 'light' | 'dark',
  badgeType: BadgeType = 'standard'
): Promise<boolean> {
  const file = new File([blob], 'chrome-dino-high-score.png', { type: 'image/png' });
  const shareUrl = buildShareUrl(badgeType);
  const shareData = {
    files: [file],
    title: 'New High Score!',
    text: `I just scored ${score.toLocaleString()} on Chrome Dino Game! Can you beat it? ${shareUrl}`,
    url: shareUrl,
  };

  // Track via GA4
  const trackShare = (method: string, success: boolean) => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'share_score', {
        'final_score': score,
        'theme_mode': theme,
        'badge_type': badgeType,
        'method': method,
        'success': success,
        'utm_campaign': shareUrl.includes('utm_campaign=') ? shareUrl.split('utm_campaign=')[1] : 'highscore',
        'timestamp': new Date().toISOString(),
      });
    }
  };

  // Use native Web Share API if available and supports files
  if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
    try {
      await navigator.share(shareData);
      trackShare('native_share', true);
      return true;
    } catch (err) {
      // User cancelled or share failed
      if ((err as Error).name !== 'AbortError') {
        console.warn('Web Share failed:', err);
      }
      trackShare('native_share', false);
      return false;
    }
  }

  // Fallback: download image directly on desktop
  try {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'chrome-dino-high-score.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    trackShare('download', true);
    return true;
  } catch (err) {
    console.warn('Download fallback failed:', err);
    trackShare('download', false);
    return false;
  }
}

/**
 * React hook that generates and shares a themed score card with one call.
 * Auto-detects badge type based on score; can be overridden.
 */
export function useShareCard() {
  const { theme } = useTheme();

  const generateAndShare = useCallback(
    async (score: number, highScore: number = 0, badgeType?: BadgeType): Promise<boolean> => {
      // Auto-detect badge type if not provided
      // Priority: explicit override > 7-day streak > 1000+ milestone > standard
      let detectedBadge: BadgeType;
      if (badgeType) {
        detectedBadge = badgeType;
      } else {
        const streakState = getStreakState();
        if (streakState.count >= 7) {
          detectedBadge = 'streak';
        } else if (score >= 1000) {
          detectedBadge = 'milestone';
        } else {
          detectedBadge = 'standard';
        }
      }
      const blob = await renderShareCard(score, highScore, theme, detectedBadge);
      if (!blob) return false;
      
      // Update Open Graph image meta tag dynamically for current page session
      try {
        const blobUrl = URL.createObjectURL(blob);
        let ogImage = document.querySelector('meta[property="og:image"]') as HTMLMetaElement | null;
        if (!ogImage) {
          ogImage = document.createElement('meta');
          ogImage.setAttribute('property', 'og:image');
          document.head.appendChild(ogImage);
        }
        ogImage.content = blobUrl;
      } catch {
        // Non-critical
      }
      
      return shareScore(blob, score, theme, detectedBadge);
    },
    [theme]
  );

  return { generateAndShare };
}

export { renderShareCard, shareScore };
